// Measurement layer: Google Tag Manager container + Google Consent Mode v2.
// - Nothing is loaded and nothing is pushed to window.dataLayer (besides the consent default) until analytics consent is granted.
// - GTM loads once per app load, only on allowed hosts, never on /admin. No direct gtag.js; GA4 is configured inside GTM.
// - Withdrawing consent sets analytics_storage=denied, disables the GA4 property runtime and clears GA cookies.
// - PII-looking params are stripped before anything reaches the dataLayer.

import { getConsent, onConsentChange } from "@/lib/consent";

const GTM_ID = process.env.REACT_APP_GTM_ID || "";
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || ""; // used only for the ga-disable flag on withdrawal, never loaded directly
const HOSTS = (process.env.REACT_APP_GTM_HOSTS || "destinoeurope.com,www.destinoeurope.com").split(",").map((h) => h.trim()).filter(Boolean);
const PII_KEYS = /^(email|e-mail|name|first_name|last_name|full_name|phone|message|content|text|question|answer|notes?|address|session_id|lead_id|client_id|id)$/i;

let loaded = false;
let lastPageView = null;

const isAdmin = () => typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
const allowedHost = () => typeof window !== "undefined" && HOSTS.includes(window.location.hostname);
export const analyticsEnabled = () => Boolean(GTM_ID) && allowedHost();
const granted = () => Boolean(getConsent()?.analytics);
const canMeasure = () => analyticsEnabled() && granted() && !isAdmin();

function dl() {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}
function gtag() { dl().push(arguments); }

function loadGtm() {
  if (loaded || !analyticsEnabled() || isAdmin()) return;
  loaded = true;
  dl().push({ "gtm.start": Date.now(), event: "gtm.js" });
  const s = document.createElement("script");
  s.id = "de-gtm";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
  document.head.appendChild(s);
}

function clearGaCookies() {
  const host = window.location.hostname;
  const domains = ["", host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];
  document.cookie.split(";").map((c) => c.split("=")[0].trim()).filter((n) => /^(_ga|_gid|_gat)/.test(n)).forEach((n) => {
    domains.forEach((d) => { document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`; });
  });
}

function applyConsent(analytics) {
  if (!analyticsEnabled()) return;
  if (analytics) {
    if (GA_ID) window[`ga-disable-${GA_ID}`] = false;
    gtag("consent", "update", { analytics_storage: "granted" });
    loadGtm();
    return;
  }
  gtag("consent", "update", { analytics_storage: "denied" });
  if (GA_ID) window[`ga-disable-${GA_ID}`] = true;
  if (loaded) { dl().push({ event: "de_consent_revoked" }); clearGaCookies(); }
  lastPageView = null;
}

export function initAnalytics() {
  if (typeof window === "undefined" || window.__deAnalyticsInit) return;
  window.__deAnalyticsInit = true;
  if (!analyticsEnabled()) return;
  gtag("consent", "default", { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied", wait_for_update: 500 });
  if (granted() && !isAdmin()) applyConsent(true);
  onConsentChange((c) => applyConsent(c.analytics));
}

const clean = (params) => Object.fromEntries(Object.entries(params || {}).filter(([k, v]) => !PII_KEYS.test(k) && (typeof v !== "string" || (!v.includes("@") && v.length <= 100))));

function safeSearch() {
  const params = new URLSearchParams(window.location.search);
  [...params.keys()].filter((k) => PII_KEYS.test(k) || /@/.test(params.get(k) || "")).forEach((k) => params.delete(k));
  const s = params.toString();
  return s ? `?${s}` : "";
}

// One page_view per consented page: current page when measurement starts, then one per route change (deduped by path+search). GTM must not add automatic/history page views.
export function trackPageView() {
  if (!canMeasure() || !loaded) return;
  const page_path = window.location.pathname + safeSearch();
  if (lastPageView === page_path) return;
  lastPageView = page_path;
  dl().push({ event: "page_view", page_path, page_location: window.location.origin + page_path, page_title: document.title });
}

export function trackEvent(name, params = {}) {
  if (!canMeasure() || !loaded) return;
  dl().push({ event: name, ...clean(params) });
}
